<template>
  <section class="translation-editor" data-testid="translation-editor" :data-resource-id="resourceId" :data-resource-type="resourceType" lang="zh-TW">
    <p v-if="!resourceId" class="notice">請先儲存中文內容取得編號，再建立英文內容。圖片與分類等關聯由中文內容維護，兩個語系共用。</p>
    <template v-else>
      <p v-if="loading" role="status">正在載入英文內容…</p>
      <p v-if="error" class="error" role="alert" data-testid="translation-error">{{ error }}</p>
      <p v-if="success" role="status" data-testid="translation-success">{{ success }}</p>
      <template v-if="management">
        <div class="status-line" data-testid="translation-status">
          <strong>英文狀態：{{ statusText }}</strong>
          <span>草稿來源：{{ originText(management.draft?.origin) }}</span>
          <span>草稿版本：{{ management.draft?.rowVersion || 0 }} ／發布版本：{{ management.published?.rowVersion || 0 }}</span>
          <span v-if="dirty" class="notice">有尚未儲存的英文內容</span>
        </div>
        <p class="notice">保存英文只更新英文草稿，並標記為人工校訂；已發布版本會保留到重新發布。圖片、分類、標籤與模板關聯共用原編號。</p>
        <p v-if="management.stale" class="notice">中文來源已變動，英文已過期；請核對目前中文來源後儲存並重新發布。</p>
        <p v-else-if="draftStale" class="notice">英文草稿使用舊的中文來源，請校訂並重新儲存後再發布。</p>
        <p v-if="management.templateDependencies?.length" class="notice">此商品引用共用模板，編號：{{ management.templateDependencies.join('、') }}。英文商品公開前，所需模板英文也必須已發布且未過期。</p>
        <details class="source-reference" data-testid="translation-source">
          <summary>查看目前中文來源與版本</summary>
          <small>來源版本：{{ management.sourceHash }}</small>
          <div v-for="field in contentFields" :key="field.key">
            <strong>{{ field.label }}（中文對照）</strong>
            <iframe v-if="field.html" :title="`${field.label}中文對照`" sandbox="" :srcdoc="previewDocument(management.source?.[field.key] || '', 'zh-TW')" />
            <p v-else class="source-text">{{ management.source?.[field.key] || '未填寫' }}</p>
          </div>
        </details>
        <div v-for="field in contentFields" :key="field.key" class="field">
          <label :for="`en-${field.key}`">{{ field.label }}（英文）</label>
          <LocalRichTextEditor v-if="field.html" :id="`en-${field.key}`" v-model="fields[field.key]" :init="editorOptions" :disabled="busy" />
          <textarea v-else-if="field.multiline" :id="`en-${field.key}`" v-model="fields[field.key]" :data-testid="`translation-${field.key}`" rows="3" :disabled="busy" />
          <input v-else :id="`en-${field.key}`" v-model="fields[field.key]" :data-testid="`translation-${field.key}`" :disabled="busy" />
        </div>
        <fieldset v-if="hasSeo" class="seo-fields">
          <legend>英文搜尋資訊（選填）</legend>
          <div class="field"><label for="en-seoTitle">搜尋標題（英文）</label><input id="en-seoTitle" data-testid="translation-seoTitle" v-model="fields.seoTitle" :disabled="busy" /><small>建議簡潔、準確；目前 {{ (fields.seoTitle || '').length }} 字元。留白時使用已發布英文名稱或標題。</small></div>
          <div class="field"><label for="en-seoDescription">搜尋摘要（英文）</label><textarea id="en-seoDescription" data-testid="translation-seoDescription" v-model="fields.seoDescription" rows="3" :disabled="busy" /><small>只可輸入純文字；留白時由已發布英文正文導出。長度提示不影響發布。</small></div>
          <button type="button" @click="clearSeo" :disabled="busy" data-testid="translation-clear-seo">清空搜尋資訊，恢復正文預設</button>
        </fieldset>
        <div class="actions">
          <button type="button" @click="saveDraft" :disabled="busy" data-testid="translation-save">{{ busy ? '處理中…' : '儲存英文草稿' }}</button>
          <button type="button" @click="showPreview = !showPreview" :disabled="busy" data-testid="translation-preview">{{ showPreview ? '收起草稿預覽' : '預覽英文草稿' }}</button>
          <button type="button" @click="publish" :disabled="busy || dirty || !management.draft" data-testid="translation-publish">發布英文</button>
          <button type="button" @click="withdraw" :disabled="busy || !management.published || management.published.withdrawn || !management.published.publishedAt" data-testid="translation-withdraw">撤回英文</button>
          <button type="button" @click="reload" :disabled="busy" data-testid="translation-reload">重新載入版本</button>
        </div>
        <p v-if="dirty" class="notice">發布前請先儲存英文草稿。</p>
        <section v-if="showPreview" class="preview" data-testid="translation-preview-panel">
          <h3>英文草稿預覽（未公開）</h3>
          <p>預覽使用目前編輯內容，不代表已發布；搜尋預覽不代表 Google 實際呈現。</p>
          <div v-if="hasSeo" class="search-preview" lang="en"><strong>{{ seoPreview.title }}</strong><p>{{ seoPreview.description }}</p></div>
          <h4 lang="en">{{ fields.name || fields.title }}</h4>
          <p v-if="fields.summary" lang="en">{{ fields.summary }}</p>
          <iframe v-if="fields.description || fields.content" title="英文草稿正文預覽" sandbox="" :srcdoc="previewDocument(fields.description || fields.content)" />
        </section>
      </template>
    </template>
  </section>
</template>

<script setup>
/* global defineProps, defineExpose, defineEmits */
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import LocalRichTextEditor from './LocalRichTextEditor.vue';
import { getTranslation, saveTranslationDraft, publishTranslation, withdrawTranslation } from '@/api/translation.api';
import { uploadArticleRichtextImage, uploadProductRichtextImage } from '@/api/file.api';

const props = defineProps({ resourceType: { type: String, required: true }, resourceId: [String, Number], sourceRevision: { type: Number, default: 0 } });
const emit = defineEmits(['updated']);
const management = ref(null);
const fields = ref({});
const baseline = ref('');
const loading = ref(false);
const busy = ref(false);
const error = ref('');
const success = ref('');
const showPreview = ref(false);
let loadSequence = 0;
const hasSeo = computed(() => ['product', 'article'].includes(props.resourceType));
const contentFields = computed(() => {
  if (props.resourceType === 'article') return [{ key: 'title', label: '標題' }, { key: 'summary', label: '摘要', multiline: true }, { key: 'content', label: '完整內容', html: true }];
  if (props.resourceType === 'product') return [{ key: 'name', label: '商品名稱' }, { key: 'description', label: '商品介紹', html: true }];
  if (props.resourceType === 'template') return [...(Object.prototype.hasOwnProperty.call(management.value?.source || {}, 'name') ? [{ key: 'name', label: '模板名稱' }] : []), { key: 'description', label: '模板介紹', html: true }];
  return [{ key: 'name', label: props.resourceType === 'article-tag' ? '標籤名稱' : '分類名稱' }];
});
const dirty = computed(() => !!management.value && JSON.stringify(fields.value) !== baseline.value);
const draftStale = computed(() => !!management.value?.draft && management.value.draft.sourceHash !== management.value.sourceHash);
const statusText = computed(() => {
  if (management.value?.stale) return '過期';
  const published = management.value?.published;
  if (published && !published.withdrawn && published.publishedAt) return management.value.availableLocales?.includes('en') ? '已發布' : '已發布，公開英文暫不可用';
  return management.value?.draft ? (draftStale.value ? '草稿（中文來源已變動）' : '草稿') : '未翻譯';
});
const originText = origin => origin === 'HUMAN' ? '人工校訂' : origin === 'AI' ? 'AI 翻譯' : '未建立';
const editorOptions = { height: 360, images_upload_handler: async blobInfo => {
  try {
    const upload = props.resourceType === 'article' ? uploadArticleRichtextImage : uploadProductRichtextImage;
    const result = await upload(blobInfo.blob());
    if (!result.url) throw new Error();
    return result.url;
  } catch { throw new Error('圖片上傳失敗，請檢查本機服務或檔案後重試。'); }
} };

function plainText(html) {
  const document = new DOMParser().parseFromString(html || '', 'text/html');
  document.querySelectorAll('script,style').forEach(node => node.remove());
  return (document.body.textContent || '').replace(/\s+/g, ' ').trim();
}
const seoPreview = computed(() => {
  const name = fields.value.name || fields.value.title || '';
  const body = props.resourceType === 'article' ? fields.value.summary || fields.value.content || name : fields.value.description || `${name}. Contact customer service to confirm the item, region and current quote.`;
  const description = plainText(body);
  return {
    title: fields.value.seoTitle?.trim() || `${plainText(name)} - Yodo Game Top-up`,
    description: fields.value.seoDescription?.trim() || (description.length <= 160 ? description : `${description.slice(0, 160).replace(/\s+\S*$/, '').trim()}...`)
  };
});
function previewDocument(html, language = 'en') {
  const document = new DOMParser().parseFromString(html || '', 'text/html');
  document.querySelectorAll('script,style,iframe,object,embed,form,link,meta,base').forEach(node => node.remove());
  document.querySelectorAll('*').forEach(node => [...node.attributes].forEach(attribute => {
    if (attribute.name.startsWith('on') || attribute.name === 'srcdoc' || (/^(href|src|action)$/i.test(attribute.name) && /^\s*(javascript|data):/i.test(attribute.value))) node.removeAttribute(attribute.name);
  }));
  return `<html lang="${language}"><head><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src http: https:; style-src 'unsafe-inline';"><style>body{font-family:sans-serif;line-height:1.7;padding:16px;color:#222}img{max-width:100%;height:auto}</style></head><body>${document.body.innerHTML}</body></html>`;
}
function acceptRepresentation(value) {
  management.value = value;
  const existing = value.draft?.fields || value.published?.fields || {};
  fields.value = Object.fromEntries(contentFields.value.map(field => [field.key, existing[field.key] || '']));
  if (hasSeo.value) Object.assign(fields.value, { seoTitle: existing.seoTitle || '', seoDescription: existing.seoDescription || '' });
  baseline.value = JSON.stringify(fields.value);
}
function showError(cause) {
  const status = cause.response?.status;
  const messages = { 400: '請確認翻譯資源及語系。', 401: '登入已失效，請重新登入。', 403: '此操作需要管理員權限。', 404: '找不到此資源，請返回列表確認。', 409: '中文來源或英文版本已變動。您的未存內容已保留；請核對並重新載入版本後再操作。', 422: '內容驗證失敗，請檢查必填欄位、HTML 安全規則、英文模板及純文字搜尋資訊。' };
  const errors = cause.response?.data?.fieldErrors || cause.response?.data?.errors;
  const labels = { name: '名稱', title: '標題', summary: '摘要', content: '完整內容', description: '介紹', seoTitle: '搜尋標題', seoDescription: '搜尋摘要', template: '商品模板' };
  const keys = Array.isArray(errors) ? errors.map(item => item.field) : errors && typeof errors === 'object' ? Object.keys(errors) : [];
  error.value = (messages[status] || '操作失敗，請檢查服務或網路後重試。') + (keys.length ? ` 需檢查：${keys.map(key => labels[key?.split('.').pop()] || '內容欄位').join('、')}。` : '');
}
async function load() {
  const sequence = ++loadSequence;
  management.value = null;
  error.value = '';
  success.value = '';
  if (!props.resourceId) return;
  loading.value = true;
  try { const result = await getTranslation(props.resourceType, props.resourceId); if (sequence === loadSequence) acceptRepresentation(result); }
  catch (cause) { if (sequence === loadSequence) showError(cause); }
  finally { if (sequence === loadSequence) loading.value = false; }
}
const versions = () => ({ expectedSourceHash: management.value.sourceHash, expectedDraftVersion: management.value.draft?.rowVersion || 0, expectedPublishedVersion: management.value.published?.rowVersion || 0 });
async function operate(action, message, preserveUnsavedFields = false) {
  const unsavedFields = preserveUnsavedFields && dirty.value ? { ...fields.value } : null;
  busy.value = true; error.value = ''; success.value = '';
  try { const result = await action(); acceptRepresentation(result); if (unsavedFields) fields.value = unsavedFields; success.value = message; emit('updated', result); }
  catch (cause) { showError(cause); }
  finally { busy.value = false; }
}
async function saveDraft() {
  if (hasSeo.value && ['seoTitle', 'seoDescription'].some(key => /<[^>]*>/.test(fields.value[key] || ''))) { error.value = '搜尋標題與搜尋摘要僅接受純文字，請移除 HTML 標記。'; return; }
  const payloadFields = { ...fields.value };
  if (hasSeo.value) ['seoTitle', 'seoDescription'].forEach(key => { payloadFields[key] = payloadFields[key]?.trim() || null; });
  await operate(() => saveTranslationDraft(props.resourceType, props.resourceId, { fields: payloadFields, ...versions(), origin: 'HUMAN' }), '英文草稿已儲存，尚未公開。');
}
async function publish() { await operate(() => publishTranslation(props.resourceType, props.resourceId, versions()), '英文已發布。'); }
async function withdraw() { await operate(() => withdrawTranslation(props.resourceType, props.resourceId, { expectedPublishedVersion: management.value.published.rowVersion }), '英文已撤回，公開前台改用中文。', true); }
function clearSeo() { fields.value.seoTitle = ''; fields.value.seoDescription = ''; }
function confirmDiscard() { return !dirty.value || window.confirm('英文內容尚未儲存，確定要捨棄修改嗎？'); }
async function reload() { if (confirmDiscard()) await load(); }
function beforeUnload(event) { if (dirty.value) { event.preventDefault(); event.returnValue = ''; } }
window.addEventListener('beforeunload', beforeUnload);
onBeforeUnmount(() => { ++loadSequence; window.removeEventListener('beforeunload', beforeUnload); });
onBeforeRouteLeave(() => confirmDiscard());
watch(() => [props.resourceType, props.resourceId], load, { immediate: true });
watch(() => props.sourceRevision, () => { if (dirty.value) error.value = '中文來源已儲存。英文未存內容保留中；請核對中文後重新載入版本。'; else load(); });
defineExpose({ confirmDiscard, dirty, reload });
</script>

<style scoped>
.translation-editor { padding: 24px; background: #20202b; border: 1px solid #575368; border-radius: 8px; color: #eee; }
.status-line,.actions { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
.status-line span { font-size: 13px; }.notice { color: #ffd391; margin: 12px 0; }.error { color: #ff9898; margin: 12px 0; }
.field { margin: 20px 0; }.field label,.field small { display: block; margin: 8px 0; }.field input,.field textarea { width: 100%; padding: 10px; color: #eee; background: #15151f; border: 1px solid #666; border-radius: 4px; }
button { padding: 10px 14px; background: #42625a; color: #fff; border: 1px solid #83988f; border-radius: 4px; cursor: pointer; }button:disabled { opacity: .5; cursor: default; }
.source-reference { margin: 20px 0; padding: 15px; border: 1px solid #666; }.source-reference summary { cursor: pointer; }.source-text { white-space: pre-wrap; }.source-reference strong { display: block; margin-top: 14px; }
iframe { width: 100%; min-height: 260px; border: 1px solid #777; background: #fff; }.seo-fields { padding: 14px; border: 1px solid #777; }.preview { margin-top: 22px; }.preview h3,.preview h4,.preview p { margin: 12px 0; }.search-preview { background: #fff; padding: 16px; color: #444; }.search-preview strong { color: #1a0dab; }
</style>
